import * as React from 'react'
import Button from '@doordash/component-button'
import Form from '../../../utilities/Form'
import s from '../../molecules/residential-address/strings'
import { noop } from 'lodash'
import { Select, TextField } from '@doordash/component-fields'
import { useEffect, useState } from 'react'
import { validators as sharedValidators } from '../../../utilities/validators'
import { Inset, StackChildren } from '@doordash/design-language'
import {
  AUSTRALIAN_STATES,
  countriesForDropDown,
} from '../../../utilities/countries'
import { TemplateResponseData } from '../../../components/organisms/workflow-template/types'
import { GENDERS } from '../../../utilities/genders'
import { FormattedDivider } from '../../../components/atoms/formatted-divider/FormattedDivider'
import { OnboardingComponentProps } from '.'

export type PlaceOfBirthType = {
  country: string
  state: string
  city: string
}

export type GenderTypes = {
  gender: string
}

export const AUSTRALIA_COUNTRY_CODE = 'AUS'

const NEW_PLACE_OF_BIRTH = {
  country: AUSTRALIA_COUNTRY_CODE,
  state: 'ACT',
  city: '',
}

const NEW_GENDER = {
  gender: '',
}

export const PlaceOfBirthComponent: React.FC<OnboardingComponentProps> = ({
  onSubmitStep,
}) => {
  const [placeOfBirth, setPlaceOfBirth] = useState<PlaceOfBirthType>({
    ...NEW_PLACE_OF_BIRTH,
  })
  const [gender, setGender] = useState<GenderTypes>({
    ...NEW_GENDER,
  })
  const [country, setCountry] = useState<string>(placeOfBirth.country)
  const [submitted, setSubmitted] = useState<boolean>(false)

  const fields = {
    country: placeOfBirth.country,
    state: placeOfBirth.state,
    city: placeOfBirth.city,
    gender: gender.gender,
  }

  const isCountryAU = country === AUSTRALIA_COUNTRY_CODE

  const validators = {
    country: sharedValidators.address.country,
    state: isCountryAU ? sharedValidators.address.state : noop,
    city: isCountryAU ? sharedValidators.address.city : noop,
    gender: sharedValidators.gender,
  }

  const generatePlaceOfBirthResponseData = (placeOfBirth, gender) => {
    const templateResponse: TemplateResponseData = {
      INPUT_TYPE_PLACE_OF_BIRTH_INPUT: {
        json_data: {
          ...placeOfBirth,
        },
      },
      INPUT_TYPE_GENDER_INPUT: {
        json_data: {
          ...gender,
        },
      },
    }

    return templateResponse
  }

  useEffect(() => {
    const finalPlaceOfBirth = { ...placeOfBirth }
    const finalGender = { ...gender }
    if (submitted) {
      onSubmitStep(
        generatePlaceOfBirthResponseData(finalPlaceOfBirth, finalGender)
      )
    }
  }, [onSubmitStep, submitted, placeOfBirth, gender])

  const submit = async data => {
    setSubmitted(true)
    const { gender, ...placeOfBirthData } = data
    setPlaceOfBirth({
      ...placeOfBirthData,
    })
    setGender({
      gender,
    })
  }

  return (
    <Form onSubmit={submit} validators={validators} fields={fields}>
      {(fields, handleChange, handleSubmit) => (
        <form onSubmit={handleSubmit}>
          <StackChildren>
            <Select
              description={s.required()}
              label={s.country()}
              onChange={value => {
                setCountry(value)
                handleChange('country', value)
                handleChange(
                  'state',
                  value === AUSTRALIA_COUNTRY_CODE ? 'ACT' : ''
                )
                handleChange('city', '')
              }}
              options={countriesForDropDown}
              {...fields.country}
            />
            <TextField
              description={isCountryAU ? s.required() : noop}
              placeholder={s.city()}
              onChange={value => {
                handleChange('city', value)
              }}
              value={fields.city.value}
              label={s.city()}
              {...fields.city}
            />
            {isCountryAU && (
              <Select
                description={s.required()}
                label={s.state()}
                onChange={value => {
                  handleChange('state', value)
                }}
                options={AUSTRALIAN_STATES}
                {...fields.state}
              />
            )}
            {!isCountryAU && (
              <TextField
                placeholder={s.state()}
                onChange={value => {
                  handleChange('state', value)
                }}
                value={fields.state.value}
                label={s.state()}
                {...fields.state}
              />
            )}

            <FormattedDivider />

            <Select
              description={s.required()}
              label="Gender"
              onChange={value => {
                handleChange('gender', value)
              }}
              options={GENDERS}
              {...fields.gender}
            />
            <Inset horizontalSize={Inset.Sizes.None}>
              <Button
                onClick={async () => {
                  try {
                    await handleSubmit()
                  } catch (e) {
                    console.warn(e)
                  }
                }}
              >
                Continue
              </Button>
            </Inset>
          </StackChildren>
        </form>
      )}
    </Form>
  )
}
